import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';

const BulkUploadModal = ({ isOpen, onClose, onUpload, categories }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileUpload = (file) => {
    setUploadedFiles(prev => [...prev, {
      file,
      name: file?.name?.replace(/\.[^/.]+$/, ''),
      category: selectedCategory,
      preview: URL.createObjectURL(file)
    }]);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleBulkUpload = () => {
    if (uploadedFiles?.length === 0) return;

    setIsProcessing(true);

    setTimeout(() => {
      const newRooms = uploadedFiles?.map((item, index) => ({
        id: Date.now() + index,
        name: item?.name,
        category: item?.category || 'Residential',
        backgroundImage: item?.preview,
        backgroundImageAlt: `${item?.name} room interior with modern flooring and natural lighting`,
        dimensions: '',
        isDefault: false,
        lightingAdjustment: 0,
        usageCount: 0,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      }));

      onUpload(newRooms);
      setIsProcessing(false);
      setUploadedFiles([]);
      onClose();
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Bulk Upload Rooms</h2>
            <p className="text-sm text-text-secondary mt-1">
              Upload multiple room backgrounds at once
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Select
            label="Default Category"
            placeholder="Select category for all uploads"
            description="This category will be applied to all uploaded rooms"
            options={categories?.map(cat => ({ value: cat, label: cat }))}
            value={selectedCategory}
            onChange={setSelectedCategory}
          />

          <FileUpload
            label="Upload Room Images"
            description="Select multiple images (max 5MB each, JPG/PNG)"
            accept="image/jpeg,image/png"
            multiple
            maxSize={5242880}
            onUpload={handleFileUpload}
            error=""
          />

          {uploadedFiles?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-text-primary">
                  Uploaded Files ({uploadedFiles?.length})
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUploadedFiles([])}
                  iconName="X"
                  iconSize={14}
                >
                  Clear All
                </Button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {uploadedFiles?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                  >
                    <div className="w-16 h-16 bg-surface rounded border border-border overflow-hidden flex-shrink-0">
                      <img
                        src={item?.preview}
                        alt={`Preview of ${item?.name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">
                        {item?.name}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {item?.category || 'No category'}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="w-8 h-8 flex items-center justify-center rounded hover:bg-surface transition-colors duration-150"
                      aria-label="Remove file"
                    >
                      <Icon name="Trash2" size={16} className="text-error" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <Icon name="Info" size={16} className="text-primary flex-shrink-0" />
            <p className="text-xs text-text-secondary">
              Room names will be automatically generated from file names. You can edit them individually after upload.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleBulkUpload}
              disabled={uploadedFiles?.length === 0}
              loading={isProcessing}
              iconName="Upload"
              iconPosition="left"
              fullWidth
            >
              Upload {uploadedFiles?.length} Room{uploadedFiles?.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;